import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherArrowLeft } from "@subframe/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useRequestPasswordResetCommandControllerRequestPasswordReset } from "@/generated/auth/auth";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { mutate: requestPasswordReset, isPending } =
    useRequestPasswordResetCommandControllerRequestPasswordReset({
      mutation: {
        onSuccess: () => {
          setIsSubmitted(true);
          toast.success("Password reset email sent successfully");
        },
        onError: (error: Error) => {
          toast.error(error?.message || "Failed to send password reset email");
        },
      },
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    requestPasswordReset({ data: { email } });
  };

  return (
    <div className="flex w-full items-center justify-center h-screen">
      {/* Left side - Testimonial */}
      <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
        <Avatar size="x-large" image="/images/testimonial-avatar.png">
          SK
        </Avatar>
        <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
          <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
            &quot;This platform has transformed how we manage compliance at our
            nursery. The interface is intuitive and saves us countless hours
            every week.&quot;
          </span>
          <div className="flex flex-wrap items-end gap-2">
            <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
              Sarah Kennedy
            </span>
            <span className="text-body font-body text-primary-rose">–</span>
            <span className="text-body font-body text-primary-rose">
              Director, Little Sprouts Nursery
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Reset password
          </span>

          <span className="text-body font-body text-subtext-color">
            Enter your email address and we&apos;ll send you a link to reset
            your password.
          </span>

          {isSubmitted ? (
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start gap-2 p-4 bg-success-50 rounded-lg">
                <span className="text-body-bold font-body-bold text-success-700">
                  Check your email
                </span>
                <span className="text-body font-body text-success-700">
                  We&apos;ve sent a password reset link to {email}
                </span>
              </div>
              <Link to="/login" className="w-full">
                <Button
                  className="h-12 w-full flex-none"
                  variant="brand-primary"
                  size="large"
                  icon={<FeatherArrowLeft />}
                >
                  Back to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex w-full flex-col items-start gap-6"
            >
              <TextField
                className="h-auto w-full flex-none"
                size="large"
                label="Email"
              >
                <TextField.Input
                  placeholder="Enter your email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  disabled={isPending}
                />
              </TextField>

              <Button
                className="h-12 w-full flex-none"
                variant="brand-primary"
                size="large"
                type="submit"
                disabled={isPending}
                loading={isPending}
              >
                Send reset link
              </Button>
            </form>
          )}

          <div className="flex w-full items-center justify-center">
            <Link to="/login">
              <LinkButton
                variant="brand"
                size="small"
                icon={<FeatherArrowLeft />}
              >
                Back to sign in
              </LinkButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
